import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getPorteroUser, porteroLogout } from '../../lib/porteroApi';
import { PorteroInstallBanner } from './PorteroInstallBanner';
import { Button } from '../ui/button';
import { Radio, Settings, LogOut, List, Home } from 'lucide-react';

export function PorteroLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getPorteroUser();
  const isAdmin = user?.rol === 'admin';
  const path = location.pathname;

  // Swap PWA manifest and meta tags for portero
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.setAttribute('href', '/portero-manifest.json');
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', '#09090b');
    const appName = document.querySelector('meta[name="application-name"]');
    if (appName) appName.setAttribute('content', 'Portero GPS');
    const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitle) appleTitle.setAttribute('content', 'Portero GPS');
    document.querySelectorAll('link[rel="apple-touch-icon"]').forEach(l => l.setAttribute('href', '/portero-icon-192.png'));
    const fav = document.querySelector('link[rel="icon"]');
    if (fav) fav.setAttribute('href', '/portero-icon-192.png');
    document.title = 'Portero GPS';
    return () => {
      if (link) link.setAttribute('href', '/manifest.json');
      if (meta) meta.setAttribute('content', '#1a1a1a');
      if (appName) appName.setAttribute('content', 'Multisistemas');
      if (appleTitle) appleTitle.setAttribute('content', 'Multisistemas');
      document.querySelectorAll('link[rel="apple-touch-icon"]').forEach(l => l.setAttribute('href', '/logo192.png'));
      if (fav) fav.setAttribute('href', '/favicon.ico');
    };
  }, []);

  const handleLogout = () => {
    porteroLogout();
    navigate('/portero');
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col">
      {/* Top bar */}
      <header className="bg-zinc-900 border-b border-zinc-800 px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center">
            <Radio className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-sm leading-tight">PORTERO GPS</h1>
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Telecontrol</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-400 hidden sm:block">{user?.nombre || user?.email}</span>
          {isAdmin && (
            <>
              <Button
                variant="ghost" size="icon"
                className={`h-8 w-8 ${path === '/portero/panel' ? 'text-emerald-400' : 'text-zinc-400 hover:text-white'}`}
                onClick={() => navigate('/portero/panel')}
                data-testid="nav-panel"
              >
                <Home className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost" size="icon"
                className={`h-8 w-8 ${path === '/portero/config' ? 'text-emerald-400' : 'text-zinc-400 hover:text-white'}`}
                onClick={() => navigate('/portero/config')}
                data-testid="nav-config"
              >
                <Settings className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost" size="icon"
                className={`h-8 w-8 ${path === '/portero/log' ? 'text-emerald-400' : 'text-zinc-400 hover:text-white'}`}
                onClick={() => navigate('/portero/log')}
                data-testid="nav-log"
              >
                <List className="h-4 w-4" />
              </Button>
            </>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-red-400" onClick={handleLogout} data-testid="portero-logout">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>

      {/* PWA Install Banner */}
      <PorteroInstallBanner />
    </div>
  );
}
